import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AppState, AppMode, BackingTrackSettings, MetronomeSettings, PartSettings,
  DEFAULT_TIME_SIGNATURE,
} from '../types';

const getDefaultPartSettings = (): PartSettings => ({
  autoSpeedUp: false,
  startBpm: 60,
  targetBpm: 120,
  increaseBpm: 5,
  pitchShift: 0,
  duration: 300, // 5 minutes default
  fixedBpm: undefined,
});

const getInitialBackingTrackSettings = (): BackingTrackSettings => {
  const stored = localStorage.getItem('backingTrackSettings');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Ensure partSettings exists for backwards compatibility
      if (!parsed.partSettings) {
        parsed.partSettings = {};
      }
      // Ensure selectedPartsByTrack exists for backwards compatibility
      if (!parsed.selectedPartsByTrack) {
        parsed.selectedPartsByTrack = {};
      }
      // Ensure selectedPartIndex is properly restored (can be null, 0, or positive number)
      if (parsed.selectedPartIndex === undefined) {
        parsed.selectedPartIndex = null;
      }
      return parsed;
    } catch {
      // If parsing fails, return default
    }
  }
  return {
    selectedTrackId: null,
    selectedPartIndex: null,
    partSettings: {},
    selectedPartsByTrack: {},
  };
};

const getInitialMetronomeSettings = (): MetronomeSettings => {
  const stored = localStorage.getItem('metronomeSettings');
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Partial<MetronomeSettings>;
      return {
        autoSpeedUp: parsed.autoSpeedUp ?? false,
        startBpm: parsed.startBpm ?? 60,
        targetBpm: parsed.targetBpm ?? 120,
        increaseBpm: parsed.increaseBpm ?? 5,
        beatsBeforeIncrease: parsed.beatsBeforeIncrease ?? 4,
        timeSignature: parsed.timeSignature ?? DEFAULT_TIME_SIGNATURE,
      };
    } catch {
      // fall through to default
    }
  }
  return {
    autoSpeedUp: false,
    startBpm: 60,
    targetBpm: 120,
    increaseBpm: 5,
    beatsBeforeIncrease: 4,
    timeSignature: DEFAULT_TIME_SIGNATURE,
  };
};

const getFreshState = (): AppState => ({
  mode: 'backingTrack',
  backingTrack: {
    selectedTrackId: null,
    selectedPartIndex: null,
    partSettings: {},
    selectedPartsByTrack: {},
  },
  metronome: {
    autoSpeedUp: false,
    startBpm: 60,
    targetBpm: 120,
    increaseBpm: 5,
    beatsBeforeIncrease: 4,
    timeSignature: DEFAULT_TIME_SIGNATURE,
    fixedBpm: undefined,
  },
  isPlaying: false,
  currentBpm: 60,
  timerEnabled: true,
  timerDuration: 300,
  timerRemaining: 300,
});

const initialState: AppState = {
  mode: (localStorage.getItem('appMode') as AppMode) || 'backingTrack',
  backingTrack: getInitialBackingTrackSettings(),
  metronome: getInitialMetronomeSettings(),
  isPlaying: false,
  currentBpm: 60,
  timerEnabled: true,
  timerDuration: 300, // 5 minutes in seconds
  timerRemaining: 300,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<AppMode>) => {
      state.mode = action.payload;
      localStorage.setItem('appMode', action.payload);
      state.isPlaying = false;
    },
    setSelectedTrack: (state, action: PayloadAction<string | null>) => {
      state.backingTrack.selectedTrackId = action.payload;
      // Restore the previously selected part for this track, if any
      if (action.payload && state.backingTrack.selectedPartsByTrack[action.payload] !== undefined) {
        state.backingTrack.selectedPartIndex = state.backingTrack.selectedPartsByTrack[action.payload];
      } else {
        state.backingTrack.selectedPartIndex = null;
      }
      localStorage.setItem('backingTrackSettings', JSON.stringify(state.backingTrack));
    },
    setSelectedPart: (state, action: PayloadAction<number | null>) => {
      state.backingTrack.selectedPartIndex = action.payload;
      // Remember this selection for the current track
      if (state.backingTrack.selectedTrackId && action.payload !== null) {
        state.backingTrack.selectedPartsByTrack[state.backingTrack.selectedTrackId] = action.payload;
      }
      localStorage.setItem('backingTrackSettings', JSON.stringify(state.backingTrack));
    },
    updatePartSettings: (state, action: PayloadAction<{ trackId: string; partIndex: number; settings: Partial<PartSettings> }>) => {
      const { trackId, partIndex, settings } = action.payload;
      
      // Initialize track if it doesn't exist
      if (!state.backingTrack.partSettings[trackId]) {
        state.backingTrack.partSettings[trackId] = {};
      }
      
      // Initialize part if it doesn't exist
      if (!state.backingTrack.partSettings[trackId][partIndex]) {
        state.backingTrack.partSettings[trackId][partIndex] = getDefaultPartSettings();
      }
      
      // Update the settings
      state.backingTrack.partSettings[trackId][partIndex] = {
        ...state.backingTrack.partSettings[trackId][partIndex],
        ...settings,
      };
      
      localStorage.setItem('backingTrackSettings', JSON.stringify(state.backingTrack));
    },
    updateMetronomeSettings: (state, action: PayloadAction<Partial<MetronomeSettings>>) => {
      state.metronome = { ...state.metronome, ...action.payload };
      localStorage.setItem('metronomeSettings', JSON.stringify(state.metronome));
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },
    setCurrentBpm: (state, action: PayloadAction<number>) => {
      state.currentBpm = action.payload;
    },
    setTimerRemaining: (state, action: PayloadAction<number>) => {
      state.timerRemaining = action.payload;
    },
    resetTimer: (state, action: PayloadAction<number | undefined>) => {
      const duration = action.payload !== undefined ? action.payload : state.timerDuration;
      state.timerRemaining = duration;
      state.timerDuration = duration;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('auth/logout', () => {
      localStorage.removeItem('appMode');
      localStorage.removeItem('backingTrackSettings');
      localStorage.removeItem('metronomeSettings');
      return getFreshState();
    });
  },
});

export const {
  setMode,
  setSelectedTrack,
  setSelectedPart,
  updatePartSettings,
  updateMetronomeSettings,
  setIsPlaying,
  setCurrentBpm,
  setTimerRemaining,
  resetTimer,
} = appSlice.actions;

export default appSlice.reducer;
